import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { api } from '../api';
import { productPath, productSlug } from '../utils/productUrl';

// ─── Styles
const DETAIL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&display=swap');

  .pd-root * { box-sizing: border-box; }
  .pd-root {
    font-family: 'Playfair Display', Georgia, serif;
    background: #f8fbfc;
    min-height: 100vh;
    color: #162033;
  }

  /* NAV */
  .pd-nav {
    position: sticky;
    top: 0;
    z-index: 50;
    background: rgba(255,255,255,0.98);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid #e8f0fa;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }
  .pd-nav-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    height: 64px;
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .pd-nav-back {
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #174f9a;
    transition: color 0.2s;
    flex-shrink: 0;
  }
  .pd-nav-back:hover { color: #2eb4b4; }
  .pd-nav-back svg { transition: transform 0.2s; }
  .pd-nav-back:hover svg { transform: translateX(-4px); }

  .pd-breadcrumb {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #666;
    font-weight: 400;
    overflow: hidden;
    flex: 1;
    min-width: 0;
  }
  .pd-breadcrumb-link {
    background: none;
    border: none;
    padding: 0;
    color: #666;
    cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 13px;
    font-weight: 400;
    transition: color 0.2s;
    white-space: nowrap;
  }
  .pd-breadcrumb-link:hover { color: #2eb4b4; text-decoration: underline; }
  .pd-breadcrumb-sep { color: #cbd5e1; flex-shrink: 0; }
  .pd-breadcrumb-cur {
    color: #162033;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pd-nav-arrows { display: flex; gap: 8px; margin-left: auto; flex-shrink: 0; }
  .pd-nav-arrow {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #e8f0fa;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #174f9a;
    transition: all 0.3s ease;
  }
  .pd-nav-arrow:hover:not(:disabled) {
    border-color: #2eb4b4;
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    color: white;
  }
  .pd-nav-arrow:disabled { opacity: 0.25; cursor: not-allowed; }

  .pd-share-wrap {
    position: relative;
    flex-shrink: 0;
  }
  .pd-share-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #e8f0fa;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #174f9a;
    transition: all 0.3s ease;
  }
  .pd-share-btn:hover,
  .pd-share-btn.active {
    border-color: #2eb4b4;
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    color: white;
  }
  .pd-share-menu {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 190px;
    padding: 8px;
    border: 1px solid #e8f0fa;
    border-radius: 12px;
    background: white;
    box-shadow: 0 18px 40px rgba(23,79,154,0.16);
    z-index: 60;
  }
  .pd-share-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: #162033;
    cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 14px;
    font-weight: 600;
    text-align: left;
    transition: background 0.2s, color 0.2s;
  }
  .pd-share-option:hover { background: #f0f7fa; color: #174f9a; }
  .pd-share-option.whatsapp { color: #128c4a; }

  /* HERO STRIPE */
  .pd-hero {
    background: linear-gradient(135deg, #2eb4b4 0%, #174f9a 100%);
    padding: 64px 24px 56px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    position: relative;
    overflow: hidden;
  }
  .pd-hero-overlay {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 70% 40%, rgba(255,255,255,0.1) 0%, transparent 60%);
    pointer-events: none;
  }
  .pd-hero-inner {
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 2;
  }
  .pd-hero-top {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }
  .pd-pill {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: 6px 16px;
    border-radius: 50px;
  }
  .pd-pill-sub  { background: rgba(255,255,255,0.2); color: white; backdrop-filter: blur(8px); }
  .pd-pill-rx   { background: rgba(220,38,38,0.95);  color: white; }
  .pd-pill-otc  { background: rgba(5,150,105,0.95);  color: white; }

  .pd-hero-brand {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.8);
    margin-bottom: 8px;
  }
  .pd-hero-title {
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 700;
    color: white;
    line-height: 1.1;
    margin-bottom: 12px;
    letter-spacing: -0.01em;
  }
  .pd-hero-tagline {
    font-size: 18px;
    font-style: italic;
    color: rgba(255,255,255,0.9);
    font-weight: 400;
  }

  /* MAIN LAYOUT */
  .pd-main {
    max-width: 1200px;
    margin: 0 auto;
    padding: 64px 24px 100px;
  }
  .pd-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 80px;
    align-items: start;
  }
  @media (max-width: 900px) {
    .pd-grid { grid-template-columns: 1fr; gap: 48px; }
  }

  /* LEFT — Image viewer */
  .pd-left {
    position: sticky;
    top: 88px;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  /* IMAGE VIEWER */
  .pd-viewer {
    border-radius: 12px;
    overflow: hidden;
    background: white;
    box-shadow: 0 8px 32px rgba(0,0,0,0.08);
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }
  .pd-viewer-stage {
    position: relative;
    overflow: hidden;
    user-select: none;
    aspect-ratio: 1 / 1;
  }
  .pd-viewer-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    will-change: transform;
  }
  .pd-viewer-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e6f7f7, #eff6ff);
  }
  .pd-viewer-fallback-text {
    font-size: 100px;
    font-weight: 700;
    color: #2eb4b4;
    opacity: 0.15;
  }

  /* Zoom controls */
  .pd-zoom-bar {
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(255,255,255,0.98);
    backdrop-filter: blur(12px);
    border-radius: 50px;
    padding: 6px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }
  .pd-zoom-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #174f9a;
    transition: background 0.2s;
  }
  .pd-zoom-btn:hover { background: #f8fbfc; }
  .pd-zoom-level {
    font-size: 12px;
    font-weight: 600;
    color: #162033;
    padding: 0 10px;
    min-width: 48px;
    text-align: center;
  }

  .pd-zoom-hint {
    position: absolute;
    bottom: 20px;
    left: 20px;
    font-size: 12px;
    color: rgba(22,32,51,0.7);
    background: rgba(255,255,255,0.9);
    backdrop-filter: blur(8px);
    padding: 6px 14px;
    border-radius: 50px;
    pointer-events: none;
    font-family: 'Playfair Display', Georgia, serif;
  }

  /* Thumb strip */
  .pd-thumb-strip {
    display: flex;
    gap: 12px;
    padding: 16px;
    background: white;
    border-top: 1px solid #f0f0f0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
    scrollbar-color: #e8f0fa transparent;
    -webkit-overflow-scrolling: touch;
  }
  .pd-thumb-strip::-webkit-scrollbar {
    height: 4px;
  }
  .pd-thumb-strip::-webkit-scrollbar-track {
    background: transparent;
  }
  .pd-thumb-strip::-webkit-scrollbar-thumb {
    background: #e8f0fa;
    border-radius: 4px;
  }
  .pd-thumb-strip::-webkit-scrollbar-thumb:hover {
    background: #2eb4b4;
  }
  .pd-thumb {
    width: 64px;
    height: 64px;
    min-width: 64px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 3px solid transparent;
    transition: border-color 0.2s;
    flex-shrink: 0;
    padding: 0;
    background: transparent;
  }
  .pd-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

  /* Meta chips */
  .pd-meta-chips { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
  .pd-meta-chip {
    font-size: 12px;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 50px;
    border: 1px solid #e8f0fa;
    background: white;
    color: #666;
  }

  /* RIGHT — Details */
  .pd-section { margin-bottom: 48px; }
  .pd-section-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .pd-section-label::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(to right, #e8f0fa, transparent);
  }
  .pd-divider { height: 1px; background: #e8f0fa; margin-bottom: 48px; }

  /* Overview */
  .pd-overview-text  { font-size: 16px; line-height: 1.75; color: #162033; font-weight: 400; margin-bottom: 16px; }
  .pd-overview-text2 { font-size: 15px; line-height: 1.75; color: #666;    font-weight: 400; }

  /* Highlights */
  .pd-highlights { display: flex; flex-direction: column; gap: 14px; }
  .pd-highlight  { display: flex; align-items: flex-start; gap: 14px; }
  .pd-highlight-icon {
    width: 28px; height: 28px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .pd-highlight-text { font-size: 15px; color: #162033; font-weight: 500; line-height: 1.6; }

  /* Composition table */
  .pd-table-wrap { border: 1px solid #e8f0fa; border-radius: 8px; overflow: hidden; }
  .pd-table { width: 100%; border-collapse: collapse; }
  .pd-table thead tr { background: linear-gradient(135deg, #2eb4b4, #174f9a); }
  .pd-table thead th {
    padding: 14px 24px;
    font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: white; text-align: left;
  }
  .pd-table thead th:last-child { text-align: right; }
  .pd-table tbody tr { border-top: 1px solid #f0f0f0; transition: background 0.15s; }
  .pd-table tbody tr:nth-child(even) { background: #fafcfd; }
  .pd-table tbody tr:hover { background: #f0f7fa; }
  .pd-table td { padding: 16px 24px; font-size: 15px; color: #162033; font-weight: 400; }
  .pd-table td:last-child { text-align: right; font-weight: 600; }

  /* Ingredients */
  .pd-ingredients { display: flex; flex-wrap: wrap; gap: 10px; }
  .pd-ingredient {
    font-size: 13px; font-weight: 500;
    padding: 8px 18px; border-radius: 50px;
    border: 2px solid; transition: all 0.2s; cursor: default;
  }

  /* Composition text block */
  .pd-composition-block {
    background: #f8fbfc;
    border: 1px solid #e8f0fa;
    border-radius: 10px;
    padding: 20px 24px;
    font-size: 14px;
    line-height: 2;
    color: #475569;
    white-space: pre-line;
  }

  /* Side effects */
  .pd-side-effects { display: flex; flex-wrap: wrap; gap: 10px; }
  .pd-side-effect {
    font-size: 13px; font-weight: 500;
    padding: 8px 18px; border-radius: 50px;
    background: #fef9e7; color: #b7791f; border: 1px solid #f9e79f;
  }

  /* Usage box */
  .pd-usage-box {
    display: flex; gap: 18px;
    padding: 24px; border-radius: 12px; border: 2px solid;
  }
  .pd-usage-icon {
    width: 44px; height: 44px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .pd-usage-text { font-size: 15px; line-height: 1.7; color: #162033; font-weight: 400; }

  /* Description block */
  .pd-desc-block {
    font-size: 15px; line-height: 1.8; color: #475569; white-space: pre-line;
  }

  /* Rx disclaimer */
  .pd-rx-disclaimer {
    margin-top: 40px;
    padding: 20px 24px;
    background: #fef2f2;
    border: 2px solid #fecaca;
    border-radius: 12px;
    display: flex; gap: 14px; align-items: flex-start;
  }
  .pd-rx-disclaimer-icon { flex-shrink: 0; margin-top: 2px; }
  .pd-rx-disclaimer-text { font-size: 14px; line-height: 1.6; color: #7f1d1d; font-weight: 400; }
  .pd-rx-disclaimer-text strong { font-weight: 700; }

  /* Product nav */
  .pd-product-nav {
    display: flex; gap: 20px;
    margin-top: 56px; padding-top: 48px;
    border-top: 2px solid #e8f0fa;
  }
  .pd-product-nav-btn {
    flex: 1; display: flex; flex-direction: column; gap: 6px;
    padding: 20px 24px;
    background: white; border: 2px solid #e8f0fa; border-radius: 12px;
    cursor: pointer; text-align: left;
    transition: all 0.3s ease;
    font-family: 'Playfair Display', Georgia, serif;
  }
  .pd-product-nav-btn:hover { border-color: #2eb4b4; background: #f8fbfc; transform: translateY(-2px); }
  .pd-product-nav-btn.right { text-align: right; align-items: flex-end; }
  .pd-product-nav-btn.disabled { opacity: 0.3; cursor: not-allowed; pointer-events: none; }
  .pd-product-nav-dir { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #174f9a; }
  .pd-product-nav-name { font-size: 18px; font-weight: 600; color: #162033; }

  /* Skeleton shimmer */
  .pd-shimmer {
    background: linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%);
    background-size: 200% 100%;
    animation: pdShimmer 1.4s infinite;
    border-radius: 8px;
  }
  @keyframes pdShimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* Error state */
  .pd-error-wrap {
    min-height: 60vh; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 40px 24px; text-align: center;
  }
  .pd-error-card {
    background: white; border-radius: 20px; border: 1px solid #e8f0fa;
    padding: 48px 32px; max-width: 320px; width: 100%;
    box-shadow: 0 4px 20px rgba(23,79,154,0.06);
  }
  .pd-error-card p { font-size: 15px; font-weight: 600; color: #475569; margin: 16px 0 24px; }
  .pd-error-btn {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    color: white; border: none; padding: 12px 28px; border-radius: 50px;
    font-family: 'Playfair Display', Georgia, serif; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: all 0.2s;
  }
  .pd-error-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(46,180,180,0.3); }

  @media (max-width: 640px) {
    .pd-main { padding: 40px 16px 80px; }
    .pd-hero { padding: 48px 16px 40px; }
    .pd-breadcrumb { display: none; }
    .pd-grid { gap: 40px; }
    .pd-left { position: relative; top: auto; }
  }
  @media (max-width: 480px) {
    .pd-nav-arrows { display: none; }
    .pd-product-nav { flex-direction: column; }
  }
`;

// ─── SVG Icons
const BackArrow = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const ChevR = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M9 18l6-6-6-6"/>
  </svg>
);
const ChevL = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M15 18l-6-6 6-6"/>
  </svg>
);
const ZoomInIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/>
  </svg>
);
const ZoomOutIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M8 11h6"/>
  </svg>
);
const ResetIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
  </svg>
);
const ShareIcon = () => (
  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49"/>
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M3 21l1.4-5.1a8.6 8.6 0 1 1 3.7 3.7L3 21z"/>
    <path d="M9.5 8.8c.2 3.1 2.6 5.4 5.7 5.7l1.4-1.4c.2-.2.2-.6-.1-.7l-1.8-.8c-.2-.1-.5 0-.7.1l-.5.5c-.9-.4-1.6-1.1-2-2l.5-.5c.2-.2.2-.5.1-.7l-.8-1.8c-.1-.3-.5-.3-.7-.1L9.5 8.8z"/>
  </svg>
);
const CopyIcon = () => (
  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const CheckSvg = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-5" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const UsageIcon = () => (
  <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);
const WarnIcon = () => (
  <svg width="18" height="18" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const NoImageIcon = () => (
  <svg width="40" height="40" fill="none" stroke="#94a3b8" strokeWidth="1.5" viewBox="0 0 24 24">
    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
    <path d="m21 15-5-5L5 21"/>
  </svg>
);

// ─── Helper: normalise image → url string
const imgUrl = (img) => (img && typeof img === 'object' ? img.url : img) || null;

// ─── Helper: parse composition string → [{name, amount}] rows
function parseComposition(raw) {
  if (!raw || typeof raw !== 'string') return [];
  const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    const m = line.match(/^(.+?)[\s]*[:\-–]\s*(.+)$/);
    if (m) {
      rows.push({ name: m[1].trim(), amount: m[2].trim() });
    } else {
      rows.push({ name: line, amount: '' });
    }
  }
  return rows;
}

// ─── Advanced Image Viewer
function ImageViewer({ images, name, accentColor, accentLight }) {
  const [activeImg,  setActiveImg ] = useState(0);
  const [zoom,       setZoom      ] = useState(1);
  const [pan,        setPan       ] = useState({ x: 0, y: 0 });
  const [dragging,   setDragging  ] = useState(false);
  const [imgError,   setImgError  ] = useState({});
  const stageRef    = useRef(null);
  const dragStart   = useRef(null);
  const pinchDist   = useRef(null);
  const touchStart  = useRef(null);

  const imgCount = images.length;

  const reset = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);
  const zoomIn = useCallback(() => setZoom(z => Math.min(z + 0.5, 5)), []);
  const zoomOut = useCallback(() => setZoom(z => {
    const n = Math.max(z - 0.5, 1);
    if (n === 1) setPan({ x: 0, y: 0 });
    return n;
  }), []);

  const onWheel = useCallback(e => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setZoom(z => {
      const n = Math.max(1, Math.min(5, z + delta));
      if (n === 1) setPan({ x: 0, y: 0 });
      return n;
    });
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const onMouseDown = useCallback(e => {
    if (zoom <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  }, [zoom, pan]);

  const onMouseMove = useCallback(e => {
    if (!dragging || !dragStart.current) return;
    setPan({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  }, [dragging]);

  const onMouseUp = useCallback(() => {
    setDragging(false);
    dragStart.current = null;
  }, []);

  const onTouchStart = useCallback(e => {
    if (e.touches.length === 1) {
      touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now(), panX: pan.x, panY: pan.y };
      pinchDist.current = null;
    }
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchDist.current = Math.hypot(dx, dy);
    }
  }, [pan]);

  const onTouchMove = useCallback(e => {
    e.preventDefault();
    if (e.touches.length === 2 && pinchDist.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const scale = dist / pinchDist.current;
      setZoom(z => Math.max(1, Math.min(5, z * scale)));
      pinchDist.current = dist;
    } else if (e.touches.length === 1 && zoom > 1 && touchStart.current) {
      const dx = e.touches[0].clientX - touchStart.current.x;
      const dy = e.touches[0].clientY - touchStart.current.y;
      setPan({ x: touchStart.current.panX + dx, y: touchStart.current.panY + dy });
    }
  }, [zoom]);

  const onTouchEnd = useCallback(e => {
    if (zoom === 1 && touchStart.current && e.changedTouches.length === 1) {
      const dx = e.changedTouches[0].clientX - touchStart.current.x;
      const dt = Date.now() - touchStart.current.t;
      if (Math.abs(dx) > 50 && dt < 350) {
        if (dx < 0 && activeImg < imgCount - 1) { setActiveImg(i => i + 1); reset(); }
        if (dx > 0 && activeImg > 0)             { setActiveImg(i => i - 1); reset(); }
      }
    }
    touchStart.current = null;
    pinchDist.current = null;
  }, [zoom, activeImg, imgCount, reset]);

  const src = imgUrl(images[activeImg]);
  const hasErr = imgError[activeImg];

  if (!imgCount) {
    return (
      <div className="pd-viewer">
        <div className="pd-viewer-stage" style={{ background: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#94a3b8' }}>
          <NoImageIcon />
          <span style={{ fontSize: 13, fontWeight: 600 }}>No images</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pd-viewer">
      <div
        ref={stageRef}
        className="pd-viewer-stage"
        style={{
          background: accentLight || '#e6f7f7',
          cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {!hasErr && src ? (
          <img
            key={activeImg}
            src={src}
            alt={name}
            onError={() => setImgError(p => ({ ...p, [activeImg]: true }))}
            draggable={false}
            className="pd-viewer-img"
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transition: dragging ? 'none' : 'transform 0.2s ease',
            }}
          />
        ) : (
          <div className="pd-viewer-fallback" style={{ background: accentLight || '#e6f7f7' }}>
            <span className="pd-viewer-fallback-text" style={{ color: accentColor || '#2eb4b4' }}>
              {name.slice(0, 2).toUpperCase()}
            </span>
          </div>
        )}

        {/* Zoom controls */}
        <div className="pd-zoom-bar">
          <button className="pd-zoom-btn" onClick={zoomOut} title="Zoom out"><ZoomOutIcon /></button>
          <span className="pd-zoom-level">{Math.round(zoom * 100)}%</span>
          <button className="pd-zoom-btn" onClick={zoomIn} title="Zoom in"><ZoomInIcon /></button>
          {zoom > 1 && (
            <button className="pd-zoom-btn" onClick={reset} title="Reset"><ResetIcon /></button>
          )}
        </div>

        {/* Dot navigation */}
        {imgCount > 1 && (
          <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8 }}>
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => { setActiveImg(i); reset(); }}
                style={{
                  width: i === activeImg ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === activeImg ? (accentColor || '#2eb4b4') : 'rgba(255,255,255,0.7)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  padding: 0,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail strip — scrollable when > 5 images */}
      {imgCount > 1 && (
        <div className="pd-thumb-strip">
          {images.map((img, i) => {
            const url = imgUrl(img);
            return (
              <button
                key={i}
                className={`pd-thumb${i === activeImg ? ' active' : ''}`}
                style={{ borderColor: i === activeImg ? (accentColor || '#2eb4b4') : 'transparent' }}
                onClick={() => { setActiveImg(i); reset(); }}
              >
                {url
                  ? <img src={url} alt={`View ${i + 1}`} />
                  : <div style={{ width: '100%', height: '100%', background: accentLight || '#e6f7f7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: accentColor || '#2eb4b4', fontWeight: 700 }}>{i + 1}</div>
                }
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Loading skeleton
function Skeleton() {
  return (
    <div className="pd-root">
      <div style={{ height: 64, background: 'white', borderBottom: '1px solid #e8f0fa' }} />
      <div style={{ height: 200, background: 'linear-gradient(135deg,#2eb4b4,#174f9a)' }} />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80 }}>
          <div>
            <div className="pd-shimmer" style={{ aspectRatio: '1/1', borderRadius: 12, marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              {[80, 110, 90].map((w, i) => (
                <div key={i} className="pd-shimmer" style={{ width: w, height: 34, borderRadius: 50 }} />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {[200, 160, 240, 180].map((h, i) => (
              <div key={i} className="pd-shimmer" style={{ height: h, borderRadius: 10 }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component
export default function ProductDetail() {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const { state: navState } = useLocation();

  const [product,   setProduct  ] = useState(null);
  const [adjacent,  setAdjacent ] = useState({ prev: null, next: null });
  const [loading,   setLoading  ] = useState(true);
  const [error,     setError    ] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [copied,    setCopied   ] = useState(false);

  const styleInjected = useRef(false);
  useEffect(() => {
    if (styleInjected.current) return;
    styleInjected.current = true;
    const el = document.createElement('style');
    el.id = 'pd-styles';
    el.textContent = DETAIL_STYLES;
    document.head.appendChild(el);
    return () => {
      const existing = document.getElementById('pd-styles');
      if (existing) existing.remove();
      styleInjected.current = false;
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    setProduct(null);
    setAdjacent({ prev: null, next: null });

    const loader = /^\d+$/.test(String(id)) ? api.product(id) : api.productBySlug(id);
    let loadedProduct = null;

    loader
      .then(data => {
        loadedProduct = data;
        setProduct(data);
        const canonicalPath = productPath(data);
        if (canonicalPath !== `/products/${id}`) {
          navigate(canonicalPath, { replace: true, state: navState });
        }
        return api.products({ division: data.division, page_size: 100 }).catch(() => null);
      })
      .then(list => {
        if (!list) return;
        const results = list.results || [];
        const idx = results.findIndex(p =>
          String(p.id) === String(loadedProduct?.id) || productSlug(p) === productSlug(loadedProduct)
        );
        setAdjacent({
          prev: idx > 0 ? results[idx - 1] : null,
          next: idx < results.length - 1 ? results[idx + 1] : null,
        });
      })
      .catch(err => setError(err.message || 'Failed to load product.'))
      .finally(() => setLoading(false));
  }, [id, navigate, navState]);

  if (loading) return <Skeleton />;

  if (error || !product) {
    return (
      <div className="pd-root">
        <div className="pd-error-wrap">
          <div className="pd-error-card">
            <NoImageIcon />
            <p>{error || 'Product not found.'}</p>
            <button className="pd-error-btn" onClick={() => navigate('/products')}>
              Back to Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const accentColor = '#2eb4b4';
  const accentLight = '#e6f7f7';
  const accentMid   = '#9ee3e3';

  const brand       = product.division_name     || product.product_category_name || '';
  const subcategory = product.product_category_name || product.product_chemical_category_name || '';
  const tagline     = product.tagline           || product.formulation || '';
  const rxRequired  = product.rx_required === true;
  const images      = product.images            || [];

  const compositionStr   = typeof product.composition === 'string' ? product.composition : '';
  const compositionRows  = parseComposition(compositionStr);

  const highlights    = product.highlights    || [];
  const keyIngredients= product.key_ingredients|| [];
  const sideEffects   = product.side_effects  || [];
  const usage         = product.usage         || product.usage_instructions || '';
  const longDesc      = product.long_description || '';
  const description   = product.description   || '';

  const { prev: prevProduct, next: nextProduct } = adjacent;
  const navigateTo = (p) => navigate(productPath(p));
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${productPath(product)}`
    : productPath(product);
  const shareText = `Check out ${product.name} on Bleen: ${shareUrl}`;
  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
    setShareOpen(false);
  };

  return (
    <div className="pd-root">

      {/* ── NAV ── */}
      <nav className="pd-nav">
        <div className="pd-nav-inner">
          <button className="pd-nav-back" onClick={() => navigate('/products')}>
            <BackArrow />
            Products
          </button>

          <div className="pd-breadcrumb">
            <button className="pd-breadcrumb-link" onClick={() => navigate('/products')}>
              Products
            </button>
            {subcategory && (
              <>
                <span className="pd-breadcrumb-sep"><ChevR /></span>
                <span style={{ whiteSpace: 'nowrap', flexShrink: 0, color: '#666' }}>{subcategory}</span>
              </>
            )}
            <span className="pd-breadcrumb-sep"><ChevR /></span>
            <span className="pd-breadcrumb-cur">{product.name}</span>
          </div>

          <div className="pd-nav-arrows">
            <button
              className="pd-nav-arrow"
              disabled={!prevProduct}
              onClick={() => prevProduct && navigateTo(prevProduct)}
              title="Previous product"
            >
              <ChevL />
            </button>
            <button
              className="pd-nav-arrow"
              disabled={!nextProduct}
              onClick={() => nextProduct && navigateTo(nextProduct)}
              title="Next product"
            >
              <ChevR />
            </button>
          </div>

          <div className="pd-share-wrap">
            <button
              className={`pd-share-btn${shareOpen ? ' active' : ''}`}
              onClick={() => setShareOpen(v => !v)}
              title="Share product"
              aria-label="Share product"
              type="button"
            >
              <ShareIcon />
            </button>
            {shareOpen && (
              <div className="pd-share-menu">
                <button className="pd-share-option whatsapp" onClick={openWhatsApp} type="button">
                  <WhatsAppIcon /> WhatsApp
                </button>
                <button className="pd-share-option" onClick={copyShareUrl} type="button">
                  <CopyIcon /> {copied ? 'Copied' : 'Copy URL'}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO STRIPE ── */}
      <div className="pd-hero">
        <div className="pd-hero-overlay" />
        <div className="pd-hero-inner">
          <div className="pd-hero-top">
            {subcategory && <span className="pd-pill pd-pill-sub">{subcategory}</span>}
            {product.status && <span className="pd-pill pd-pill-sub">{product.status}</span>}
            <span className={`pd-pill ${rxRequired ? 'pd-pill-rx' : 'pd-pill-otc'}`}>
              {rxRequired ? 'Rx Only' : 'OTC'}
            </span>
          </div>
          {brand    && <p className="pd-hero-brand">{brand}</p>}
          <h1 className="pd-hero-title">{product.name}</h1>
          {tagline  && <p className="pd-hero-tagline">"{tagline}"</p>}
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className="pd-main">
        <div className="pd-grid">

          {/* LEFT — Image viewer */}
          <div className="pd-left">
            <ImageViewer
              images={images}
              name={product.name}
              accentColor={accentColor}
              accentLight={accentLight}
            />

            <div className="pd-meta-chips">
              {product.formulation && (
                <span className="pd-meta-chip">{product.formulation}</span>
              )}
              {subcategory && (
                <span className="pd-meta-chip">{subcategory}</span>
              )}
              {product.packing_size && (
                <span className="pd-meta-chip">{product.packing_size}</span>
              )}
              {rxRequired && (
                <span className="pd-meta-chip" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }}>
                  Prescription Required
                </span>
              )}
            </div>
          </div>

          {/* RIGHT — Details */}
          <div className="pd-right">

            {(description || longDesc) && (
              <div className="pd-section">
                <p className="pd-section-label">Overview</p>
                {description && <p className="pd-overview-text">{description}</p>}
                {longDesc     && <p className="pd-overview-text2">{longDesc}</p>}
              </div>
            )}

            {(description || longDesc) && <div className="pd-divider" />}

            {highlights.length > 0 && (
              <>
                <div className="pd-section">
                  <p className="pd-section-label">Key Benefits</p>
                  <div className="pd-highlights">
                    {highlights.map((h, i) => (
                      <div key={i} className="pd-highlight">
                        <span className="pd-highlight-icon" style={{ background: accentLight }}>
                          <CheckSvg color={accentColor} />
                        </span>
                        <span className="pd-highlight-text">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pd-divider" />
              </>
            )}

            {compositionStr && (
              <>
                <div className="pd-section">
                  <p className="pd-section-label">Composition</p>
                  {compositionRows.length > 0 && compositionRows.some(r => r.amount) ? (
                    <div className="pd-table-wrap">
                      <table className="pd-table">
                        <thead>
                          <tr>
                            <th>Ingredient</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compositionRows.map((row, i) => (
                            <tr key={i}>
                              <td>{row.name}</td>
                              <td style={{ color: accentColor }}>{row.amount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="pd-composition-block">{compositionStr}</div>
                  )}
                </div>
                <div className="pd-divider" />
              </>
            )}

            {keyIngredients.length > 0 && (
              <>
                <div className="pd-section">
                  <p className="pd-section-label">Key Ingredients</p>
                  <div className="pd-ingredients">
                    {keyIngredients.map((k, i) => (
                      <span
                        key={i}
                        className="pd-ingredient"
                        style={{ background: accentLight, color: accentColor, borderColor: accentMid }}
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="pd-divider" />
              </>
            )}

            {sideEffects.length > 0 && (
              <>
                <div className="pd-section">
                  <p className="pd-section-label">Possible Side Effects</p>
                  <div className="pd-side-effects">
                    {sideEffects.map((s, i) => (
                      <span key={i} className="pd-side-effect">{s}</span>
                    ))}
                  </div>
                </div>
                <div className="pd-divider" />
              </>
            )}

            {usage && (
              <div className="pd-section">
                <p className="pd-section-label">How to Use</p>
                <div className="pd-usage-box" style={{ background: accentLight, borderColor: accentMid }}>
                  <div className="pd-usage-icon" style={{ background: accentColor }}>
                    <UsageIcon />
                  </div>
                  <p className="pd-usage-text">{usage}</p>
                </div>
              </div>
            )}

            {rxRequired && (
              <div className="pd-rx-disclaimer">
                <span className="pd-rx-disclaimer-icon"><WarnIcon /></span>
                <p className="pd-rx-disclaimer-text">
                  <strong>Prescription Required —</strong> This product is available only against a valid medical prescription. Consult your physician before use. Keep out of reach of children.
                </p>
              </div>
            )}

            {(prevProduct || nextProduct) && (
              <div className="pd-product-nav">
                <button
                  className={`pd-product-nav-btn${!prevProduct ? ' disabled' : ''}`}
                  onClick={() => prevProduct && navigateTo(prevProduct)}
                  disabled={!prevProduct}
                >
                  <span className="pd-product-nav-dir">← Previous</span>
                  <span className="pd-product-nav-name">{prevProduct?.name || '—'}</span>
                </button>
                <button
                  className={`pd-product-nav-btn right${!nextProduct ? ' disabled' : ''}`}
                  onClick={() => nextProduct && navigateTo(nextProduct)}
                  disabled={!nextProduct}
                >
                  <span className="pd-product-nav-dir">Next →</span>
                  <span className="pd-product-nav-name">{nextProduct?.name || '—'}</span>
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

    </div>
  );
}
